import { MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { getLocale } from '../../../../../utils/i18n';
import { useCalendarContext } from '../../context/CalendarContext';

export type MiniCalendarHeaderProps = PickersCalendarHeaderProps<DateTime> & {
  minDate: DateTime;
  maxDate: DateTime;
};

export function MiniCalendarHeader(props: MiniCalendarHeaderProps) {
  const { currentMonth, onMonthChange, minDate, maxDate } = props;
  const { setMiniCalendarDate } = useCalendarContext();

  // Generate year options based on min/max date constraints
  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let year = minDate.year; year <= maxDate.year; year++) {
      years.push(year);
    }
    return years;
  }, [minDate, maxDate]);

  // Generate month options, constrained when at min or max year
  const monthOptions = useMemo(() => {
    const months: { value: number; label: string }[] = [];
    const startMonth = currentMonth.year === minDate.year ? minDate.month : 1;
    const endMonth = currentMonth.year === maxDate.year ? maxDate.month : 12;

    const locale = getLocale();
    for (let month = startMonth; month <= endMonth; month++) {
      const date = DateTime.local(currentMonth.year, month, 1).setLocale(locale);
      months.push({
        value: month,
        label: date.toLocaleString({ month: 'long' }),
      });
    }
    return months;
  }, [currentMonth.year, minDate, maxDate]);

  const handleMonthChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      const newDate = currentMonth.set({ month: event.target.value as number });
      onMonthChange(newDate, 'left');
      setMiniCalendarDate(newDate);
    },
    [currentMonth, onMonthChange, setMiniCalendarDate],
  );

  const handleYearChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      const newDate = currentMonth.set({ year: event.target.value as number });
      onMonthChange(newDate, 'left');
      setMiniCalendarDate(newDate);
    },
    [currentMonth, onMonthChange, setMiniCalendarDate],
  );

  return (
    <div className="flex items-center justify-center gap-6 px-3 py-2">
      <Select
        value={currentMonth.month}
        onChange={handleMonthChange}
        size="small"
        variant="standard"
        disableUnderline
        renderValue={(value) => (
          <Typography variant="h3">
            {monthOptions.find((m) => m.value === value)?.label}
          </Typography>
        )}
      >
        {monthOptions.map((month) => (
          <MenuItem key={month.value} value={month.value}>
            {month.label}
          </MenuItem>
        ))}
      </Select>

      <Select
        value={currentMonth.year}
        onChange={handleYearChange}
        size="small"
        variant="standard"
        disableUnderline
        renderValue={(value) => <Typography variant="h3">{value}</Typography>}
      >
        {yearOptions.map((year) => (
          <MenuItem key={year} value={year}>
            {year}
          </MenuItem>
        ))}
      </Select>
    </div>
  );
}

